import { variant, onTerms, match } from 'variant';
import type { GVariantOf } from 'variant';
import {
  useAsync as useReactAsync,
  useAsyncCallback as useReactAsyncCallback,
} from 'react-async-hook';
import type { UseAsyncOptions, UseAsyncReturn } from 'react-async-hook';
import { useEffect, useMemo } from 'react';
import { useExit } from './use-exit.js';

export const AsyncStatus = variant(
  onTerms(({ T: _T }) => ({
    Error: (error: Error) => ({ error }),
    Success: (result: typeof _T) => ({ result }),
    Loading: () => ({}),
  }))
);

export const AsyncCallbackStatus = variant(
  onTerms(({ T: _T }) => ({
    Error: (error: Error) => ({ error }),
    Success: (result: typeof _T) => ({ result }),
    Loading: () => ({}),
    NotRequested: () => ({}),
  }))
);

export type AsyncStatus<T> = GVariantOf<
  typeof AsyncStatus,
  undefined,
  { T: T }
>;

export type AsyncCallbackStatus<T> = GVariantOf<
  typeof AsyncCallbackStatus,
  undefined,
  { T: T }
>;

type UseCLIAsync = <R, Q extends unknown[]>(
  fn: (options: { signal: AbortSignal }) => Promise<R>,
  args: Q,
  options?: UseAsyncOptions<R>
) => { status: AsyncStatus<R>; merge: UseAsyncReturn<R, Q>['merge'] };

type UseCLIAsyncCallback = <R, Q extends unknown[]>(
  fn: (options: { signal: AbortSignal }, ...args: Q) => Promise<R>,
  options?: UseAsyncOptions<R>
) => {
  status: AsyncCallbackStatus<R>;
  merge: UseAsyncReturn<R, Q>['merge'];
  execute: UseAsyncReturn<R, Q>['execute'];
};

export const useAsync: UseCLIAsync = (fn, args, options) => {
  const exit = useExit();
  const abortController = useMemo(() => new AbortController(), []);
  const { result, error, status, merge } = useReactAsync(
    fn.bind(null, { signal: abortController.signal }),
    args,
    {
      onError: (e) => exit(e),
      ...(options ?? {}),
    }
  );

  useEffect(() => {
    return () => {
      abortController.abort();
    };
  }, [abortController]);

  return match(status, {
    error: () => ({
      status: AsyncStatus.Error(
        error ?? new Error('Unexpected error state in useAsync')
      ),
      merge,
    }),
    'not-requested': () => ({ status: AsyncStatus.Loading(), merge }),
    loading: () => ({ status: AsyncStatus.Loading(), merge }),
    // result could be undefined, if async function do not return async. We check all other states and here we just typecast to type from async function.
    success: () => ({ status: AsyncStatus.Success(result!), merge }),
  });
};

export const useAsyncCallback: UseCLIAsyncCallback = (fn, options) => {
  const exit = useExit();
  const abortController = useMemo(() => new AbortController(), []);
  const { result, error, status, merge, execute } = useReactAsyncCallback(
    fn.bind(null, { signal: abortController.signal }),
    {
      onError: (e) => exit(e),
      ...(options ?? {}),
    }
  );

  useEffect(() => {
    return () => {
      abortController.abort();
    };
  }, [abortController]);

  return match(status, {
    error: () => ({
      status: AsyncCallbackStatus.Error(
        error ?? new Error('Unexpected error state in useAsyncCallback')
      ),
      merge,
      execute,
    }),
    'not-requested': () => ({
      status: AsyncCallbackStatus.NotRequested(),
      merge,
      execute,
    }),
    loading: () => ({ status: AsyncCallbackStatus.Loading(), merge, execute }),

    success: () => ({
      // result could be undefined, if async function do not return async. We check all other states and here we just typecast to type from async function.
      status: AsyncCallbackStatus.Success(result!),
      merge,
      execute,
    }),
  });
};
